import React, { useState, useRef, useEffect, useContext } from "react";
import { uploadDocument, setPortoModuleComplete } from "../context/utils";
import { AppContext } from "../context";
import {
  exitFullscreenAndUnlock,
  enterFullscreenLandscape,
} from "../utils_new/orientation";
import cameraImg from "../img/camera.png";
import { startVideoPlayback } from "../utils_new/videoPlayback";

/** The mime type a data: URI declares, e.g. "application/pdf". "" when absent. */
const dataUriMime = (uri) => {
  const match = String(uri).match(/^data:([^;,]+)/);
  return match ? match[1] : "";
};

/** Byte count of a data: URI payload, for the size shown beside the file name. */
const dataUriBytes = (uri) => {
  const body = String(uri).split(",")[1] || "";
  const padding = (body.match(/=+$/) || [""])[0].length;
  return Math.max(0, Math.floor((body.length * 3) / 4) - padding);
};

const formatBytes = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * data: URI -> Blob, so a file can be handed to <object> as a blob: URL.
 *
 * Chrome and the Android WebView refuse to render data:application/pdf in an
 * embedded frame -- the same bytes behind a blob: URL are accepted, which is the
 * only way the viewer can show the contents of an uploaded PDF rather than a
 * placeholder card.
 */
const dataUriToBlob = (uri) => {
  const [head, body = ""] = String(uri).split(",");
  const mime =
    (head.match(/^data:([^;]+)/) || [])[1] || "application/octet-stream";
  const binary = atob(body);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

const App = () => {
  const {
    stepCovered,
    setScreen,
    config,
    setCurrentPage,
    inspectionId,
    mandatory,
    setMandatory,
    setCondition,
    currentBlock,
    condition,
    clientId,
    setStepCovered,
    docs,
    setDocs,
    showErrorPopup,
    setShowErrorPopup,
    popupContent,
    setPopupContent,
    previewItem,
    setPreviewItem,
    isCameraOpen,
    setIsCameraOpen,
    activeDocId,
    setActiveDocId,
    stream,
    setStream,
    isFormValid,
    setIsFormValid,
  } = useContext(AppContext);

  // Max files per document, configurable via the `documentMaxLimit` config key.
  // Falls back to 5 when the key or its value is not available.
  const MAX_LIMIT =
    (Object.keys(config).includes("documentMaxLimit") &&
      config["documentMaxLimit"]) ||
    5;

  // Document categories are configurable via the `documentCategories` config key.
  // Fall back to {} when the key or its value is not available.
  const documentCategories =
    (Object.keys(config).includes("documentCategories") &&
      config["documentCategories"]) ||
    {};
  // Guarded list for safe iteration (config value may be {} or a non-array).
  const documentCategoriesList = Array.isArray(documentCategories)
    ? documentCategories
    : [];

  // Returns the translated string for `text` from config["language"] when present,
  // otherwise falls back to the original text. Mirrors the pattern used across the app.
  const getTranslateText = (text) => {
    if (
      config &&
      config["language"] &&
      Object.keys(config["language"]).includes(text)
    ) {
      return config["language"][text];
    }
    return text;
  };

  // Tracks whether files came from 'camera' or 'upload' picker.
  // Keys are derived from the configured document categories so they stay in sync.
  const [docSource, setDocSource] = useState(() =>
    documentCategoriesList.reduce(
      (acc, cat) => ({ ...acc, [cat.id]: null }),
      {}
    )
  );

  // Holds the captured image temporarily for full-screen preview
  const [tempImage, setTempImage] = useState(null);

  // File names, parallel to docs[id] by index. docs itself carries nothing but
  // base64 -- handleMainClick and uploadDocument both depend on that -- so the names
  // travel alongside it. Each name is appended in the same event as its data, so
  // one cannot drift onto the wrong file when several FileReaders finish out of
  // order.
  const [docNames, setDocNames] = useState({});

  // blob: URL of the file open in the viewer, for anything that is not an image.
  const [previewUrl, setPreviewUrl] = useState(null);

  // NEW: Tracks if the camera stream is actually visible on screen
  const [isCameraReady, setIsCameraReady] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // This screen is read and filled in rather than aimed at a car, so it drops the
  // fullscreen landscape lock the inspection flow runs under and lays out for
  // whichever way the phone is held. The lock is restored on the way out --
  // submit, back, or the component being unmounted for any other reason -- so the
  // rest of the flow still gets the orientation it expects.
  useEffect(() => {
    exitFullscreenAndUnlock();
    // Backstop only: submit and back restore it inside their own click, because a
    // fullscreen request outside a user gesture is refused. This catches every
    // other way the screen can be left.
    return () => {
      enterFullscreenLandscape();
    };
  }, []);

  // A blob: URL for the open file, revoked as soon as another file is opened or
  // the viewer closes, so nothing is held after the overlay goes away.
  useEffect(() => {
    const data = previewItem && previewItem.data;
    if (!data || dataUriMime(data).startsWith("image/")) {
      setPreviewUrl(null);
      return undefined;
    }
    let url = null;
    try {
      url = URL.createObjectURL(dataUriToBlob(data));
    } catch (e) {
      // Malformed base64: the viewer falls back to showing the name alone.
      url = null;
    }
    setPreviewUrl(url);
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [previewItem]);

  /**
   * What to call a file in the viewer. An upload keeps the name it arrived with
   * and a capture gets the one minted when it was accepted; a file already in
   * docs from an earlier visit to this screen has no recorded name, so it falls
   * back to one derived from its type and position.
   */
  const fileNameFor = (docId, index, data) => {
    const recorded = Array.isArray(docNames[docId])
      ? docNames[docId][index]
      : null;
    if (recorded) return recorded;
    const mime = dataUriMime(data);
    const isImage = mime.startsWith("image/");
    const subtype = (mime.split("/")[1] || "bin").split("+")[0];
    return `${isImage ? "Image" : "Document"}_${index + 1}.${
      subtype === "jpeg" ? "jpg" : subtype
    }`;
  };

  /** Append a name at the same index its data lands at. */
  const addName = (docId, name) =>
    setDocNames((prev) => ({
      ...prev,
      [docId]: Array.isArray(prev[docId]) ? [...prev[docId], name] : [name],
    }));

  useEffect(() => {
    const allRequiredFilled = documentCategoriesList
      .filter((cat) => cat.required)
      .every((cat) => Array.isArray(docs[cat.id]) && docs[cat.id].length > 0);
    setIsFormValid(allRequiredFilled);
  }, [docs]);

  const triggerWarning = (title, message) => {
    setPopupContent({ title, message });
    setShowErrorPopup(true);
  };

  const handleMainClick = () => {
    if (!isFormValid) {
      triggerWarning(
        getTranslateText("Missing Documents"),
        getTranslateText(
          "Please upload all mandatory documents (*) before proceeding."
        )
      );
    } else {
      Object.keys(docs).forEach((key) => {
        // ADDED SAFETY CHECK: Ensure it is an array before iterating
        if (Array.isArray(docs[key])) {
          docs[key].forEach((item, index) => {
            let d = {};
            let counter = index + 1;

            if (item.startsWith("data:application/pdf")) {
              d["pdfData"] = item;
              uploadDocument(clientId, inspectionId, d, `${key} ${counter}`);
            } else {
              d["imgData"] = item;
              uploadDocument(clientId, inspectionId, d, `${key} ${counter}`);
            }
          });
        }
      });
      // console.log("Final Base64 Data:", docs);
      if (currentBlock["mandatory"]) {
        let temp1 = mandatory;
        temp1[currentBlock["id"]] = "completed";
        setMandatory(temp1);
      }
      let temp = condition;
      temp[currentBlock["id"]] = "completed";
      setCondition(temp);
      setPortoModuleComplete(currentBlock["tag"]);
      // Restore here rather than relying on the unmount cleanup: requestFullscreen
      // is only granted inside a user gesture, and this click is one.
      enterFullscreenLandscape();
      setScreen("menu");
    }
  };

  const startCamera = async (id) => {
    if (docSource[id] === "upload") {
      triggerWarning(
        getTranslateText("Action Blocked"),
        getTranslateText(
          "You have already uploaded files. Please remove them to use the camera."
        )
      );
      return;
    }
    // Added safety check in case docs[id] is undefined
    if (Array.isArray(docs[id]) && docs[id].length >= MAX_LIMIT) {
      triggerWarning(
        getTranslateText("Limit Reached"),
        getTranslateText("You can only capture a maximum of # items.").replace(
          "#",
          MAX_LIMIT
        )
      );
      return;
    }
    setActiveDocId(id);
    setIsCameraOpen(true);
    setTempImage(null);
    setIsCameraReady(false); // Reset readiness when opening

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: 1280, height: 720 },
        audio: false,
      });
      setStream(mediaStream);
      if (videoRef.current) videoRef.current.srcObject = mediaStream;
      startVideoPlayback(videoRef.current, config);
    } catch (err) {
      setIsCameraOpen(false);
    }
  };

  const handlePdfUpload = (e, id) => {
    if (docSource[id] === "camera") {
      triggerWarning(
        getTranslateText("Action Blocked"),
        getTranslateText(
          "You have already captured images. Please remove them to upload files."
        )
      );
      e.target.value = null;
      return;
    }

    const files = Array.from(e.target.files);
    // Added safety check
    const currentLength = Array.isArray(docs[id]) ? docs[id].length : 0;
    
    if (currentLength + files.length > MAX_LIMIT) {
      triggerWarning(
        getTranslateText("Limit Reached"),
        getTranslateText("Total files cannot exceed #.").replace(
          "#",
          MAX_LIMIT
        )
      );
      e.target.value = null;
      return;
    }

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setDocs((prev) => ({
          ...prev,
          // Safely spread the array, or create a new one if it didn't exist
          [id]: Array.isArray(prev[id]) ? [...prev[id], base64String] : [base64String],
        }));
        // Same event as the data above, so both land at the same index.
        addName(id, file.name || "");
        setDocSource((prevSource) => ({ ...prevSource, [id]: "upload" }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = null;
  };

  const takePhoto = () => {
    // Extra safety guard
    if (!isCameraReady) return;

    // Added safety check
    if (Array.isArray(docs[activeDocId]) && docs[activeDocId].length >= MAX_LIMIT) {
      stopCamera();
      triggerWarning(
        getTranslateText("Limit Reached"),
        getTranslateText("Maximum limit reached.")
      );
      return;
    }
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);
      const imageData = canvas.toDataURL("image/jpeg", 0.8);

      setTempImage(imageData);
    }
  };

  const acceptPhoto = () => {
    const shotNumber =
      (Array.isArray(docs[activeDocId]) ? docs[activeDocId].length : 0) + 1;
    setDocs((prev) => ({
      ...prev,
      // Safely spread the array
      [activeDocId]: Array.isArray(prev[activeDocId]) ? [...prev[activeDocId], tempImage] : [tempImage],
    }));
    // A capture has no name of its own, so give it one the viewer can show.
    addName(activeDocId, `Capture ${shotNumber}.jpg`);
    setDocSource((prevSource) => ({
      ...prevSource,
      [activeDocId]: "camera",
    }));
    setTempImage(null);
  };

  const rejectPhoto = () => {
    setTempImage(null);
  };

  const stopCamera = () => {
    if (stream) stream.getTracks().forEach((track) => track.stop());
    setStream(null);
    setIsCameraOpen(false);
    setTempImage(null);
    setIsCameraReady(false); // Clean up readiness
  };

  const removeFile = (docId, index) => {
    if (Array.isArray(docs[docId]) && docs[docId].length === 1) {
      setDocSource((prevSource) => ({ ...prevSource, [docId]: null }));
    }

    setDocs((prev) => {
      if (!Array.isArray(prev[docId])) return prev; // Safety check
      const newList = [...prev[docId]];
      newList.splice(index, 1);
      return { ...prev, [docId]: newList };
    });
    // Keep the names lined up with the data they belong to.
    setDocNames((prev) => {
      if (!Array.isArray(prev[docId])) return prev;
      const names = [...prev[docId]];
      names.splice(index, 1);
      return { ...prev, [docId]: names };
    });
    setPreviewItem(null);
  };

  const backBtn = () => {
    // Same as submit: restore the flow's fullscreen landscape inside the gesture.
    enterFullscreenLandscape();
    if (["opes_prod", "opes_uat"].includes(clientId)) {
      setScreen("menuOpes");
    } else {
      setScreen("menu");
    }
  };

  // --- display-only values for the header pill and the submit label ---------
  // Derived from docs on each render; nothing here decides anything, the gate is
  // still isFormValid from the effect above.
  const requiredCategories = documentCategoriesList.filter(
    (cat) => cat.required
  );
  const totalRequired = requiredCategories.length;
  const uploadedRequired = requiredCategories.filter(
    (cat) => Array.isArray(docs[cat.id]) && docs[cat.id].length > 0
  ).length;

  // Same primary colour as every other button in the app, navy as the fallback.
  const accent =
    (config && config["colors"] && config["colors"]["btn"]) || "#1c1550";

  // --- the file open in the viewer ------------------------------------------
  const previewName = previewItem
    ? fileNameFor(previewItem.id, previewItem.index, previewItem.data)
    : "";
  const previewMime = previewItem ? dataUriMime(previewItem.data) : "";
  const previewIsImage = previewMime.startsWith("image/");
  const previewMeta = previewItem
    ? [
        previewMime === "application/pdf"
          ? "PDF"
          : previewIsImage
            ? getTranslateText("Image")
            : previewMime,
        formatBytes(dataUriBytes(previewItem.data)),
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <div style={{ position: "relative", height: "100dvh", width: "100dvw" }}>
      <>
        <div style={styles.appWrapper}>
          {previewItem && (
            <div className="doc-view" style={{ "--doc-accent": accent }}>
              {/* The name of the file being looked at, above the file itself. */}
              <div className="doc-view-bar">
                <div className="doc-view-name" title={previewName}>
                  {previewName}
                </div>
                {previewMeta && (
                  <div className="doc-view-meta">{previewMeta}</div>
                )}
              </div>

              <div className="doc-view-body">
                {previewIsImage ? (
                  <img
                    className="doc-view-img"
                    src={previewItem.data}
                    alt={previewName}
                  />
                ) : previewUrl ? (
                  /* <object> renders the file wherever the browser can, and shows
                     its children wherever it cannot -- the Android WebView has no
                     PDF viewer, so there the name and an open link stand in. */
                  <object
                    className="doc-view-frame"
                    data={previewUrl}
                    type={previewMime || "application/pdf"}
                    aria-label={previewName}
                  >
                    <div className="doc-view-fallback">
                      <span className="doc-view-badge">
                        {getTranslateText("PDF")}
                      </span>
                      <p className="doc-view-fallback-name">{previewName}</p>
                      <a
                        className="doc-view-open"
                        href={previewUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {getTranslateText("Open file")}
                      </a>
                    </div>
                  </object>
                ) : (
                  <div className="doc-view-fallback">
                    <span className="doc-view-badge">
                      {getTranslateText("File")}
                    </span>
                    <p className="doc-view-fallback-name">{previewName}</p>
                  </div>
                )}
              </div>

              <div className="doc-view-nav">
                <button
                  className="doc-view-btn doc-view-back"
                  onClick={() => setPreviewItem(null)}
                >
                  {getTranslateText("Back")}
                </button>
                <button
                  className="doc-view-btn doc-view-remove"
                  onClick={() => removeFile(previewItem.id, previewItem.index)}
                >
                  {getTranslateText("Remove")}
                </button>
              </div>
            </div>
          )}

          {showErrorPopup && (
            <div
              style={styles.popupOverlay}
              onClick={() => setShowErrorPopup(false)}
            >
              <div style={styles.popupBox} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ margin: "10px 0", color: "#1A1446" }}>
                  {popupContent.title}
                </h3>
                <p style={{ color: "#666", fontSize: "14px" }}>
                  {popupContent.message}
                </p>
                <button
                  style={styles.closePopupButton}
                  onClick={() => setShowErrorPopup(false)}
                >
                  {getTranslateText("Got it")}
                </button>
              </div>
            </div>
          )}

          {isCameraOpen && (
            <div className="doc-cam" style={{ "--doc-accent": accent }}>
              {/* The live frame and the shot of it share one box and one fit, so
                  the captured image sits exactly where the preview was, whichever
                  way the phone is held -- nothing shifts or re-crops between
                  pressing the shutter and looking at the result. */}
              <div className="doc-cam-stage">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  onLoadedData={() => setIsCameraReady(true)}
                  className="doc-cam-media"
                  style={{ display: tempImage ? "none" : "block" }}
                />
                {tempImage && (
                  <img
                    src={tempImage}
                    className="doc-cam-media"
                    alt="captured preview"
                  />
                )}

                {/* The app's standard retake / accept icons, the same pair every
                    other capture screen shows, centred along the bottom of the
                    picture rather than pinned to one side of it. */}
                {tempImage && (
                  <div className="doc-cam-reel">
                    <i
                      className="fas fa-times fa-2x vin-btn-retake"
                      onClick={rejectPhoto}
                      aria-label={getTranslateText("Retake")}
                      title={getTranslateText("Retake")}
                    ></i>
                    <i
                      className="fas fa-check fa-2x vin-btn-next"
                      onClick={acceptPhoto}
                      aria-label={getTranslateText("Use photo")}
                      title={getTranslateText("Use photo")}
                    ></i>
                  </div>
                )}
              </div>
              <canvas ref={canvasRef} style={{ display: "none" }} />

              {/* The rail belongs to the live camera: down the right-hand edge
                  in landscape, across the bottom in portrait. Reviewing a shot
                  hands the whole screen to the picture instead. */}
              {!tempImage && (
                <div className="doc-cam-rail">
                  <div className="doc-cam-count">
                    {/* Safely display the length */}
                    {Array.isArray(docs[activeDocId]) ? docs[activeDocId].length : 0} / {MAX_LIMIT}
                  </div>

                  <div
                    className="doc-cam-shutter"
                    style={{
                      backgroundImage: `url(${cameraImg})`,
                      opacity: isCameraReady ? 1 : 0.5,
                      pointerEvents: isCameraReady ? "auto" : "none",
                    }}
                    onClick={takePhoto}
                  ></div>

                  <button className="doc-cam-done" onClick={stopCamera}>
                    {getTranslateText("Done")}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="doc-page" style={{ "--doc-accent": accent }}>
            <div className="doc-topbar">
              <button
                className="doc-icon-btn"
                onClick={backBtn}
                aria-label={getTranslateText("Back")}
                title={getTranslateText("Back")}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <h1 className="doc-title">
                {getTranslateText("Document Upload")}
              </h1>
              <div
                className={`doc-progress-pill ${
                  totalRequired > 0 && uploadedRequired === totalRequired
                    ? "doc-complete"
                    : ""
                }`}
              >
                {uploadedRequired} / {totalRequired}{" "}
                {getTranslateText("uploaded")}
              </div>
            </div>

            <div className="doc-grid">
              {documentCategoriesList.map((doc) => {
                const source = docSource[doc.id];
                const isCamDisabled = source === "upload";
                const isUploadDisabled = source === "camera";
                // Safety check before mapping over the docs array
                const docArray = Array.isArray(docs[doc.id]) ? docs[doc.id] : [];
                const isDone = docArray.length > 0;

                return (
                  <div
                    key={doc.id}
                    className={`doc-card ${isDone ? "doc-done" : ""}`}
                  >
                    <div className="doc-card-top">
                      <div className="doc-card-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M7 3h7l4 4v14H7z" />
                          <path d="M14 3v4h4M9 12h6M9 16h6" />
                        </svg>
                      </div>
                      <div className="doc-label">
                        {doc.label}
                        {doc.required && <span className="doc-req">*</span>}
                      </div>
                      {/* Count sits top-right so the row below is free for the
                          thumbnails at full height. */}
                      <span className="doc-status">
                        {isDone && (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        )}
                        {isDone
                          ? `${docArray.length} / ${MAX_LIMIT}`
                          : doc.required
                            ? getTranslateText("Mandatory")
                            : getTranslateText("Optional")}
                      </span>
                    </div>

                    <div className="doc-card-bottom">
                      <div className="doc-thumbs">
                        {docArray.map((base64, i) => (
                          <div
                            key={i}
                            className="doc-thumb"
                            title={fileNameFor(doc.id, i, base64)}
                            onClick={() =>
                              setPreviewItem({
                                data: base64,
                                id: doc.id,
                                index: i,
                              })
                            }
                          >
                            {base64.includes("image/") ? (
                              <img src={base64} alt="snap" />
                            ) : (
                              <span className="doc-thumb-pdf">
                                {getTranslateText("PDF")}
                              </span>
                            )}
                            <div
                              className="doc-thumb-del"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFile(doc.id, i);
                              }}
                            >
                              ×
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="doc-actions">
                        <button
                          className="doc-mini-btn"
                          title={getTranslateText("Camera")}
                          onClick={() => startCamera(doc.id)}
                          style={{ opacity: isCamDisabled ? 0.4 : 1 }}
                        >
                          <svg viewBox="0 0 48 48" fill="currentColor">
                            <path d="M37.8964844,39.953125H10.1035156c-3.2421875,0-5.8798828-2.6376953-5.8798828-5.8798828V20.1293945 c0-3.2421875,2.6376953-5.8798828,5.8798828-5.8798828h2.9741211c0.5141602,0,0.9658203-0.3291016,1.1235352-0.8183594 l0.484375-1.5043945c0.7470703-2.3212891,2.8876953-3.8803711,5.3256836-3.8803711h7.9780273 c2.4375,0,4.578125,1.559082,5.3251953,3.8798828l0.484375,1.5053711c0.1572266,0.4892578,0.6083984,0.8178711,1.1230469,0.8178711 h2.9746094c3.2421875,0,5.8798828,2.6376953,5.8798828,5.8798828v13.9438477 C43.7763672,37.3154297,41.1386719,39.953125,37.8964844,39.953125z M10.1035156,17.2495117 c-1.5878906,0-2.8798828,1.2919922-2.8798828,2.8798828v13.9438477c0,1.5878906,1.2919922,2.8798828,2.8798828,2.8798828h27.7929688 c1.5878906,0,2.8798828-1.2919922,2.8798828-2.8798828V20.1293945c0-1.5878906-1.2919922-2.8798828-2.8798828-2.8798828H34.921875 c-1.8212891,0-3.4208984-1.1650391-3.9785156-2.8989258l-0.484375-1.5048828 c-0.3466797-1.0761719-1.3388672-1.7993164-2.4697266-1.7993164h-7.9780273c-1.1308594,0-2.1235352,0.7231445-2.4702148,1.7998047 l-0.484375,1.5048828c-0.5585938,1.7338867-2.1577148,2.8984375-3.9790039,2.8984375H10.1035156z M24,35.0322266 c-4.8515625,0-8.7988281-3.9472656-8.7988281-8.7988281c0-4.8520508,3.9472656-8.7993164,8.7988281-8.7993164 s8.7988281,3.9472656,8.7988281,8.7993164C32.7988281,31.0849609,28.8515625,35.0322266,24,35.0322266z M24,20.434082 c-3.1972656,0-5.7988281,2.6015625-5.7988281,5.7993164c0,3.1972656,2.6015625,5.7988281,5.7988281,5.7988281 s5.7988281-2.6015625,5.7988281-5.7988281C29.7988281,23.0356445,27.1972656,20.434082,24,20.434082z" />
                          </svg>
                        </button>

                        <div
                          onClick={() =>
                            isUploadDisabled &&
                            triggerWarning(
                              getTranslateText("Action Blocked"),
                              getTranslateText(
                                "Remove captured images to enable file upload."
                              )
                            )
                          }
                        >
                          <label
                            className="doc-mini-btn doc-outline"
                            title={getTranslateText("Upload")}
                            style={{ opacity: isUploadDisabled ? 0.4 : 1 }}
                          >
                            <svg viewBox="0 0 100 100" fill="currentColor">
                              <path fillRule="evenodd" d="m78.125 31.594c0-2.9844-1.0664-5.8711-3.0078-8.1367l-13.688-15.969c-2.375-2.7695-5.8438-4.3633-9.4922-4.3633h-36.312c-6.9023 0-12.5 5.5977-12.5 12.5v62.5c0 6.9023 5.5977 12.5 12.5 12.5h33.23c-1.5508-1.9023-2.8711-4-3.9141-6.25h-29.316c-3.4531 0-6.25-2.7969-6.25-6.25v-62.5c0-3.4531 2.7969-6.25 6.25-6.25h36.312c0.40234 0 0.80078 0.039062 1.1875 0.11328v12.387c0 5.1758 4.1992 9.375 9.375 9.375h9.3672c0.003906 0.11328 0.007812 0.22656 0.007812 0.34375v10.594c2.1445 0 4.2344 0.22656 6.25 0.66016zm-9.918-6.5938-8.832-10.301v7.1758c0 1.7266 1.3984 3.125 3.125 3.125z" />
                              <path d="m43.176 57.75c0.58203 0.54297 1.3477 0.84375 2.1406 0.84375 2.8164-5.6211 7.3633-10.223 12.941-13.105l6.5898-7.4844c1.1367-1.2969 1.0117-3.2734-0.28125-4.4141-1.2969-1.1367-3.2734-1.0117-4.4141 0.28125l-15.055 17.113-10.148-9.5156c-0.66406-0.62109-1.5625-0.92188-2.4688-0.82812-0.90234 0.097656-1.7188 0.58203-2.2383 1.332l-8.3672 12.086v-22.809c0-1.7266-1.3984-3.125-3.125-3.125s-3.125 1.3984-3.125 3.125v43.75c0 1.7266 1.3984 3.125 3.125 3.125h24.098c-0.43359-2.0156-0.66016-4.1055-0.66016-6.25h-20.312v-6.8359l11.445-16.531z" />
                              <path d="m68.75 81.25c0 1.7266 1.3984 3.125 3.125 3.125s3.125-1.3984 3.125-3.125v-11.004l2.4375 2.5312c1.1953 1.2422 3.1758 1.2773 4.418 0.082031 1.2422-1.1992 1.2812-3.1758 0.082031-4.418l-7.8125-8.1094c-0.58984-0.61328-1.4023-0.95703-2.25-0.95703s-1.6602 0.34375-2.25 0.95703l-7.8125 8.1094c-1.1992 1.2422-1.1602 3.2188 0.082031 4.418 1.2422 1.1953 3.2227 1.1602 4.418-0.082031l2.4375-2.5312z" />
                              <path fillRule="evenodd" d="m71.875 46.875c13.809 0 25 11.191 25 25s-11.191 25-25 25-25-11.191-25-25 11.191-25 25-25zm18.75 25c0-10.355-8.3945-18.75-18.75-18.75s-18.75 8.3945-18.75 18.75 8.3945 18.75 18.75 18.75 18.75-8.3945 18.75-18.75z" />
                            </svg>
                            <input
                              type="file"
                              accept=".pdf, image/*"
                              multiple
                              disabled={isUploadDisabled}
                              onChange={(e) => handlePdfUpload(e, doc.id)}
                              hidden
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="doc-footer">
              <button
                className={`doc-submit-btn ${isFormValid ? "doc-enabled" : ""}`}
                onClick={handleMainClick}
                /* Inert until every required document is in, matching both the
                   reference and the previous behaviour, where pointerEvents:none
                   meant an invalid click did nothing. */
                disabled={!isFormValid}
              >
                {isFormValid
                  ? getTranslateText("Submit")
                  : `${getTranslateText("Submit")} (${uploadedRequired}/${totalRequired})`}
              </button>
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

const styles = {
  appWrapper: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "#fff",
    fontFamily: "sans-serif",
    overflowX: "hidden",
  },
  mainContent: { padding: "20px", maxWidth: "800px", margin: "0 auto" },
  mainTitle: {
    margin: "0 0 20px 0",
    fontSize: "24px",
    fontWeight: "600",
    textAlign: "center",
    color: "var(--app-text, black)",
  },
  stackContainer: { display: "flex", flexDirection: "column", gap: "14px" },
  fullWidthCard: {
    background: "#fff",
    borderRadius: "12px",
    padding: "15px",
    border: "1px solid #eef2f7",
    boxShadow: "0 2px 6px rgba(0,0,0,0.02)",
  },
  cardInfo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px",
  },
  cardLabel: { fontWeight: "700", fontSize: "15px", color: "var(--app-text, #333)" },
  btnGroup: { display: "flex", gap: "8px", alignItems: "center" },
  camAction: {
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    fontWeight: "bold",
    fontSize: "14px",
    backgroundColor: "#1A1446",
    color: "#fff",
    cursor: "pointer",
  },
  pdfAction: {
    padding: "3px 31px",
    borderRadius: "8px",
    fontWeight: "bold",
    fontSize: "14px",
    backgroundColor: "#fff",
    color: "#1A1446",
    border: "2px solid #1A1446",
    cursor: "pointer",
    display: "inline-block",
  },
  previewScroll: {
    display: "flex",
    gap: "12px",
    marginTop: "15px",
    overflowX: "auto",
    paddingTop: "10px",
    borderTop: "1px solid #f2f2f2",
    minHeight: "60px",
    alignItems: "center",
  },
  previewItem: {
    position: "relative",
    width: "70px",
    height: "55px",
    flexShrink: 0,
  },
  imgFill: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "6px",
    border: "1px solid #1A1446",
  },
  pdfText: {
    width: "100%",
    height: "100%",
    background: "#fff",
    color: "#1A1446",
    border: "1px solid #1A1446",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontWeight: "bold",
  },
  delBtn: {
    position: "absolute",
    top: "-8px",
    right: "-8px",
    background: "#1A1446",
    color: "#fff",
    borderRadius: "50%",
    width: "20px",
    height: "20px",
    textAlign: "center",
    lineHeight: "18px",
    fontSize: "14px",
    border: "1px solid #fff",
  },
  emptyMessage: { color: "#999", fontSize: "12px" },

  popupOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 12000,
  },
  popupBox: {
    backgroundColor: "#fff",
    padding: "30px",
    borderRadius: "20px",
    maxWidth: "300px",
    textAlign: "center",
  },
  closePopupButton: {
    marginTop: "20px",
    width: "100%",
    padding: "12px",
    backgroundColor: "#1A1446",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontWeight: "bold",
  },
  finalSubmit: {
    width: "40%",
    margin: "30px auto",
    display: "block",
    padding: "18px",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontWeight: "bold",
    fontSize: "16px",
  },

};

export default App;